import React, { useRef, useCallback } from "react";
import { useDrag, DragSourceMonitor } from "react-dnd";
import { cn } from "../../lib/utils";
import {
  PenLine,
  TextCursorInput,
  User,
  Briefcase,
  CalendarDays,
  MapPin,
  Building2,
  Mail,
  FileText,
  Landmark,
} from "lucide-react";

export type FieldType =
  | "signature"
  | "position"
  | "date"
  | "city"
  | "province"
  | "state"
  | "initial"
  | "name"
  | "email"
  | "text"
  | "custom";

interface DraggableFieldProps {
  type: FieldType;
  label: string;
  id?: string;
  recipientId?: string;
  isPlaced?: boolean;
  className?: string;
  onRemove?: () => void;
  readOnly?: boolean;
  signatureImage?: string; // URL to signature image if available
  fullName?: string; // Fallback for signature if no image available
  iconColor?: string; // Custom color class for the icon
  customFieldId?: number;
}

interface DragItem {
  type: FieldType;
  label: string;
  id: string;
  recipientId?: string;
  iconColor?: string;
  customFieldId?: number;
}

const fieldIcons = {
  signature: PenLine,
  position: Briefcase,
  date: CalendarDays,
  city: MapPin,
  province: Building2,
  state: Landmark,
  initial: TextCursorInput,
  name: User,
  email: Mail,
  text: User,
  custom: FileText,
};

export const fieldTypeColors: Record<string, string> = {
  signature: "text-indigo-600",
  initial: "text-violet-600",
  date: "text-amber-600",
  name: "text-blue-600",
  email: "text-teal-600",
  city: "text-rose-600",
  position: "text-orange-600",
};

const DraggableField: React.FC<DraggableFieldProps> = ({
  type,
  label,
  id,
  recipientId,
  isPlaced = false,
  className,
  onRemove,
  readOnly = false,
  signatureImage,
  fullName,
  iconColor,
  customFieldId,
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [{ isDragging }, dragRef] = useDrag<DragItem, unknown, { isDragging: boolean }>(() => ({
    type: isPlaced ? "placed-field" : "field",
    item: { type, label, id: id || `${type}-${Date.now()}`, recipientId, iconColor, customFieldId },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
    canDrag: !readOnly,
  }), [isPlaced, readOnly, type, label, id, recipientId, iconColor, customFieldId]);

  // Store dragRef in a mutable ref so the stable callback always uses the latest
  const dragRefLatest = useRef(dragRef);
  dragRefLatest.current = dragRef;

  // Stable callback ref - never changes, so React never detaches/reattaches it
  const combinedRef = useCallback((node: HTMLDivElement | null) => {
    (elementRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    dragRefLatest.current(node);
  }, []);

  const Icon = fieldIcons[type];

  // Render signature content (image or fallback text)
  const renderSignatureContent = () => {
    if (type === "signature") {
      if (signatureImage) {
        return (
          <div className="w-full h-full flex items-center justify-center overflow-hidden">
            <img 
              src={signatureImage} 
              alt="Signature" 
              className="max-w-full max-h-full object-contain"
            />
          </div>
        );
      } else if (fullName) {
        return (
          <div 
            className="w-full h-full flex items-center justify-center text-primary"
            style={{ 
              fontFamily: "'Dancing Script', cursive",
              fontSize: "1.25rem",
              lineHeight: "1"
            }}
          >
            {fullName}
          </div>
        );
      }
    }
    
    // Default rendering for non-signature fields or signature without image/name
    const isHexColor = iconColor?.startsWith('#');
    return (
      <>
        <Icon
          className={cn("h-4 w-4 shrink-0", readOnly ? "text-muted-foreground" : !isHexColor ? (iconColor || fieldTypeColors[type] || "text-primary") : "")}
          style={isHexColor && !readOnly ? { color: iconColor } : undefined}
        />
        <span className={cn("text-sm font-medium", readOnly ? "text-muted-foreground" : "")}>{label}</span>
      </>
    );
  };

  // Build dynamic style for recipient color tint on placed fields
  const dynamicStyle: React.CSSProperties = {
    minWidth: isPlaced ? "120px" : "auto",
    minHeight: isPlaced ? "40px" : "auto",
  };

  if (isPlaced && !readOnly && iconColor) {
    dynamicStyle.backgroundColor = `${iconColor}20`;
    dynamicStyle.borderColor = iconColor;
  }

  return (
    <div
      ref={combinedRef}
      className={cn(
        "flex items-center gap-2 p-2 rounded-md border",
        isPlaced
          ? readOnly 
            ? "bg-secondary/20 cursor-not-allowed border-border"
            : iconColor ? "cursor-move" : "bg-primary/10 cursor-move border-border"
          : "bg-background hover:bg-accent cursor-grab border-border",
        isDragging ? "opacity-50" : "opacity-100",
        className
      )}
      style={dynamicStyle}
    >
      {renderSignatureContent()}
      
      {isPlaced && onRemove && !readOnly && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ml-auto text-destructive hover:text-destructive/70 flex items-center justify-center h-5 w-5 rounded-full"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      )}
    </div>
  );
};

export default DraggableField;
